(ns practitest.frontend.common.dom
  (:require ["compute-scroll-into-view" :as scroll-compute]
            [clojure.string :as str]
            [practitest.frontend.components.icons :as icons]))

(defn prevent-default
  [e]
  (when e
    (.preventDefault e)))

(defn stop-propagation
  [e]
  (when e
    (.stopPropagation e)))

(defn event-value
  [e]
  (when-let [target (some-> e (.-target))]
    (if (= "checkbox" (.-type target))
      (.-checked target)
      (.-value target))))

(defn classes-pairs [& pairs]
  (assert (even? (count pairs)))

  (->> pairs
       (partition 2)
       (reduce
         (fn [a [k v]]
           (if v
             (conj a (if (keyword? k)
                       (name k)
                       k))
             a))
         [])
       (str/join " ")))

(defn- process-class-name
  [class]
  (if (keyword? class)
    (name class)
    class))

;; Produces vector instead of string
(defn classes-pairs-v [& pairs]
  (assert (even? (count pairs)))

  (->> pairs
       (partition 2)
       (reduce
         (fn [a [k v]]
           (if v
             (conj a (process-class-name k))
             a))
         [])))

(defn append-class
  "Appends class or classes to existing class list

  existing classes should be vector (can be string, but it's discouraged)
  new-classes can be either vector or string/keyword"
  [existing-classes new-classes]
  (let [existing-classes (if (string? existing-classes)
                           (do
                             (js/console.warn "String passed to existing-classes - you do not want this")
                             (str/split existing-classes #"\s"))
                           existing-classes)]
    (if (coll? new-classes)
      (apply conj
             (vec existing-classes)
             (map process-class-name new-classes))
      (conj existing-classes (process-class-name new-classes)))))

(defn get-item [key]
  (.getItem (.-localStorage js/window) key))

(defn set-item [key value]
  (.setItem (.-localStorage js/window) key value))

(defn remove-item [key]
  (.removeItem (.-localStorage js/window) key))

(defn get-session-item [key]
  (.getItem (.-sessionStorage js/window) key))

(defn set-session-item [key value]
  (.setItem (.-sessionStorage js/window) key value))

(defn remove-session-item [key]
  (.removeItem (.-sessionStorage js/window) key))

(defn get-root-node
  [node]
  (when node
    (or (.-body (.getRootNode node))
        ;; Shadow DOM support
        (.getRootNode node))))

(defn in-shadow-dom? [node]
  (when node
    (instance? js/ShadowRoot (.getRootNode node))))

(defn reset-root-ref!
  [container-ref node]
  (reset! container-ref (get-root-node node)))

(defn root-ref-handler
  [container-ref]
  ;; TODO: reagent/partial ?
  (partial reset-root-ref! container-ref))

(defn ref-handler
  "Handles React refs using either atoms or callback functions.
  In case atom is provided it will be used as storage for react ref.
  function will be passed as is to component."
  [ref-value]
  (when ref-value
    (cond
      (satisfies? IAtom ref-value)
      (partial reset! ref-value)

      (fn? ref-value)
      ref-value

      :else
      (js/console.warn "Only atoms and functions are supported as `ref` values"))))

(defn focus-on-ref!
  "Focuses on node using `.focus` call if ref is present and is populated.

  * `ref` - ref to focus on - nils are handled gracefully, derefable is expected
  * `immediate?` - flag to indicate whether focus should be immediate (e.g. in ref handler when dom node
     is already present in document tree) or delayed (e.g. in on-click handler when node sync to DOM is actually
     delayed by react or when some other node is grabbing focus)"
  [ref immediate?]
  (when ref
    (let [focus-fn #(when-some [v @ref]
                     (.focus v))]
      (if immediate?
        (focus-fn)
        (js/setTimeout focus-fn)))))

(defn get-current-path
  "Returns PATH part of currently show page (will return '/foo' for 'http://host:port/foo?query=1#fragment')"
  []
  (let [location (.-location js/window)]
    (.-pathname location)))

(defn get-current-href-relative
  "Returns full relative href of currently shown page
  (will return '/foo?query=1#fragment' for 'http://host:port/foo?query=1#fragment'"
  []
  (let [location (.-location js/window)]
    (str (.-pathname location)
         (.-search location)
         (.-hash location))))

(defn open-in-new-tab! [path]
  (.open js/window path "_blank"))

(defn navigate!
  [path]
  (set! js/window.location path))

;; TODO: use this in other places too
(defn csrf-token []
  (some-> js/document
          (.querySelector "meta[name='csrf-token']")
          (.getAttribute "content")))

(defn form-navigate!
  [path params]
  (let [form (js/document.createElement "form")]
    (set! (.-visibility (.-style form)) "hidden")
    (set! (.-display (.-style form)) "none")
    (set! (.-method form) "post")
    (set! (.-action form) path)
    (let [input (js/document.createElement "input")]
      (set! (.-name input) "authenticity_token")
      (set! (.-value input) (csrf-token))
      (.appendChild form input))

    (doseq [[k v] (seq params)]
      (let [input (js/document.createElement "input")]
        (set! (.-name input) k)
        (set! (.-value input) v)
        (.appendChild form input)))

    (js/document.body.appendChild form)
    (.submit form)))

(defn post-navigate!
  [path]
  (form-navigate! path {}))

;;ported from practitest-app
(defn text->clipboard [text]

  (let [ta (doto (js/document.createElement "textarea")
             (.setAttribute "style" "position: fixed; top: 0; left: 0; width: 2em; height: 2em; padding: 0; border: none; outline: none; boxShadow: none; background: transparent;")
             (js/document.body.appendChild))]
    (set! (.-value ta) text)
    (.focus ta)
    (.select ta)
    (js/document.execCommand "copy")
    (js/document.body.removeChild ta)
    text))

(defn add-test-id
  "Add stable identifiers (supposed to be used with E2E testing tools and product tours).

  Do not use those identifiers for development-specific tasks."
  [props test-id]
  (cond-> props

          (some? test-id)
          (assoc
            ;; This id is used by automated intercom product tour building tool - it detects this
            ;; attribute and builds selectors correctly (even in shadow DOM)
            ;; For some reason Intercom doesn't work with `id` or `data-testid` attributes
            :data-intercom-target test-id

            ;; This id is used by automated cypress/playwright test build tools
            :data-testid test-id)))

(defn add-test-id-and-id
  "Same as `add-test-id` but (pending refactoring of keyboard shortcuts), adds `id` attribute to element as well"
  [props test-id]
  (cond-> (add-test-id props test-id)

          ;; This HTML id is used in selectors of keyboard shortcuts
          (some? test-id)
          (assoc :id test-id)))

(defn set-title
  "Sets page title to specified parameter"
  [title]
  (set! (.-title js/document) title))

(defn update-page-title
  "Practitest specific title setter"
  [title]
  (set-title (str "PractiTest - " title)))

(defn set-favicon! []
  (let [data-url (str "data:image/svg+xml," (js/encodeURIComponent icons/favicon-svg))
        existing (.querySelector js/document "link[rel~='icon']")
        link (or existing
                 (let [el (.createElement js/document "link")]
                   (.appendChild (.-head js/document) el)
                   el))]
    (set! (.-rel link) "icon")
    (set! (.-type link) "image/svg+xml")
    (set! (.-href link) data-url)))

(defn height-overflow? [node]
  (> (.-scrollHeight node) (.-offsetHeight node)))

(defn width-overflow? [node]
  (> (.-scrollWidth node) (.-offsetWidth node)))

(defn scroll-if-needed
  [node]
  (when node
    (let [actions (scroll-compute/compute node
                                          #js {:scrollMode "if-needed"
                                               :block "start"
                                               :inline "nearest"})]

      (doseq [a (array-seq actions)]
        (set! (.-scrollTop (.-el a)) (.-top a))
        (set! (.-scrollLeft (.-el a)) (.-left a))))))

(defn scroll-to-ref-if-needed
  [container-ref]
  (when container-ref
    (scroll-if-needed @container-ref)))

(defn reload!
  []
  (some-> (.-location js/window)
          (.reload)))
